import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { TrendingUp, Users, Package, Star, ArrowUp, ArrowDown, Minus } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#D4AF37';
const COLORS = ['#D4AF37', '#C5D8F9', '#F9C5D1', '#3DDC97', '#FFCC66', '#FF5C6C'];

export default function AdminAnalytics() {
  const [overview, setOverview] = useState(null);
  const [products, setProducts] = useState(null);
  const [periods, setPeriods] = useState(null);
  const [clients, setClients] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND}/api/analytics/overview`).then(r => r.json()),
      fetch(`${BACKEND}/api/analytics/produits`).then(r => r.json()),
      fetch(`${BACKEND}/api/analytics/periodes`).then(r => r.json()),
      fetch(`${BACKEND}/api/analytics/clients`).then(r => r.json()),
    ]).then(([ov, pr, pe, cl]) => {
      setOverview(ov);
      setProducts(pr);
      setPeriods(pe);
      setClients(cl);
    }).catch((err) => { /* eslint-disable-line no-empty */ }).finally(() => setLoading(false));
  }, []);

  const TABS = [
    { id: 'overview', label: 'Vue d\'ensemble' },
    { id: 'produits', label: 'Produits' },
    { id: 'periodes', label: 'Saisonnalité' },
    { id: 'clients', label: 'Clients' },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="px-3 py-2 rounded-lg" style={{ background: '#1C1C1C', border: '1px solid #2A2A2A' }}>
          <p className="text-xs font-medium" style={{ color: 'var(--admin-text)' }}>{label}</p>
          {payload.map((p, i) => (
            <p key={`col-${i}`} className="text-xs" style={{ color: p.color }}>{p.name}: {p.value}</p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 rounded-full" style={{ border: '2px solid #2A2A2A', borderTopColor: '#D4AF37' }} /></div>;

  return (
    <div data-testid="admin-analytics">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Paiements &amp; Factures</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Analytics &amp; CA</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Performances commerciales, produits, clients, saisonnalité</p>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, padding: 4, background: 'var(--admin-surface)', borderRadius: 12 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ flex: 1, padding: '8px 4px', borderRadius: 9, fontSize: 12.5, fontWeight: 600,
              cursor: 'pointer', border: 'none', transition: 'background .15s',
              background: activeTab === t.id ? 'var(--admin-gold)' : 'transparent',
              color: activeTab === t.id ? '#2a1d05' : 'var(--admin-muted)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeTab === 'overview' && overview && (
        <div>
          {/* KPI grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'CA Total', value: `${overview.kpis.ca_total.toFixed(0)}€`, icon: <TrendingUp size={18} />, color: GOLD },
              { label: 'Panier moyen', value: `${overview.kpis.panier_moyen.toFixed(0)}€`, icon: <Package size={18} />, color: '#C5D8F9' },
              { label: 'Taux conversion', value: `${overview.kpis.taux_conversion}%`, icon: <ArrowUp size={18} />, color: '#3DDC97' },
              { label: 'Note moyenne', value: overview.kpis.note_moyenne ? `${overview.kpis.note_moyenne}/5 ★` : 'N/A', icon: <Star size={18} />, color: '#F9C5D1' },
            ].map((k, i) => (
              <div key={`col-${i}`} className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                <div className="flex items-center justify-between mb-2">
                  <span style={{ color: k.color }}>{k.icon}</span>
                </div>
                <p className="text-2xl font-bold font-mono-ose" style={{ color: k.color }}>{k.value}</p>
                <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{k.label}</p>
              </div>
            ))}
          </div>

          {/* Secondary KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Devis créés', value: overview.kpis.nb_devis },
              { label: 'Devis signés', value: overview.kpis.nb_signes },
              { label: 'Clients', value: overview.kpis.nb_clients },
              { label: 'Avis clients', value: overview.kpis.nb_avis },
            ].map((k, i) => (
              <div key={`review-${i}`} className="p-3 rounded-xl text-center" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                <p className="text-xl font-bold font-mono-ose" style={{ color: 'var(--admin-text)' }}>{k.value}</p>
                <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{k.label}</p>
              </div>
            ))}
          </div>

          {/* CA 30j vs 90j */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
              <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--admin-gold)' }}>CA par période</h3>
              <div className="space-y-3">
                {[
                  { label: '30 derniers jours', value: overview.kpis.ca_30_jours, color: '#3DDC97' },
                  { label: '90 derniers jours', value: overview.kpis.ca_90_jours, color: 'var(--admin-gold)' },
                  { label: 'Total', value: overview.kpis.ca_total, color: '#C5D8F9' },
                ].map((r, i) => (
                  <div key={`col-${i}`} className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{r.label}</span>
                    <span className="font-mono-ose font-bold" style={{ color: r.color }}>{r.value.toFixed(2)}€</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
              <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--admin-gold)' }}>Entonnoir commercial</h3>
              <div className="space-y-3">
                {[
                  { label: 'Devis créés', value: overview.kpis.nb_devis, color: '#C5D8F9', pct: 100 },
                  { label: 'Devis signés', value: overview.kpis.nb_signes, color: 'var(--admin-gold)', pct: overview.kpis.taux_conversion },
                  { label: 'Acompte payé', value: overview.kpis.nb_paye, color: '#3DDC97', pct: overview.kpis.taux_paiement },
                ].map((r, i) => (
                  <div key={`col-${i}`}>
                    <div className="flex justify-between text-xs mb-1">
                      <span style={{ color: 'var(--admin-muted)' }}>{r.label}</span>
                      <span style={{ color: r.color }}>{r.value} ({r.pct}%)</span>
                    </div>
                    <div className="h-1.5 rounded-full" style={{ background: '#2A2A2A' }}>
                      <div className="h-full rounded-full" style={{ width: `${Math.min(r.pct, 100)}%`, background: r.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PRODUITS */}
      {activeTab === 'produits' && products && (
        <div>
          {products.produits.length === 0 ? (
            <div className="p-8 rounded-xl text-center" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
              <Package size={32} className="mx-auto mb-2" style={{ color: 'rgba(255,255,255,0.15)' }} />
              <p className="text-sm" style={{ color: 'var(--admin-faint)' }}>Aucune donnée de vente disponible</p>
              <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.2)' }}>Des statistiques apparaîtront après les premières réservations payées</p>
            </div>
          ) : (
            <div>
              {/* Chart */}
              <div className="p-4 rounded-xl mb-4" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--admin-gold)' }}>Produits par CA généré</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={products.produits.slice(0, 8)} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                    <XAxis dataKey="product_nom" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} angle={-20} textAnchor="end" height={40} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="ca_total" fill={GOLD} name="CA (€)" radius={4} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Table */}
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #2A2A2A' }}>
                <div className="flex gap-2 px-4 py-2 text-xs" style={{ background: '#1C1C1C', color: 'var(--admin-muted)' }}>
                  <span className="flex-1">Produit</span>
                  <span className="w-20 text-right">Réserv.</span>
                  <span className="w-24 text-right">CA</span>
                  <span className="w-24 text-right">Tarif moyen</span>
                </div>
                {products.produits.slice(0, 10).map((p, i) => (
                  <div key={`product-${i}`} className="flex items-center gap-2 px-4 py-2.5" style={{ background: i % 2 === 0 ? '#161616' : '#121212', borderBottom: '1px solid rgba(42,42,42,0.5)' }}>
                    <p className="flex-1 text-sm truncate" style={{ color: 'var(--admin-text)' }}>{p.product_nom}</p>
                    <p className="w-20 text-right text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{p.nb_reservations}</p>
                    <p className="w-24 text-right font-mono-ose text-sm" style={{ color: 'var(--admin-gold)' }}>{p.ca_total.toFixed(0)}€</p>
                    <p className="w-24 text-right text-xs" style={{ color: 'var(--admin-muted)' }}>{p.tarif_moyen.toFixed(0)}€</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* PERIODES */}
      {activeTab === 'periodes' && periods && (
        <div className="space-y-4">
          {/* Monthly */}
          {periods.monthly?.length > 0 ? (
            <div className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
              <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--admin-gold)' }}>Devis par mois</h3>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={periods.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                  <XAxis dataKey="mois" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="nb_devis" stroke={GOLD} strokeWidth={2} dot={false} name="Devis" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="p-6 rounded-xl text-center" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
              <p className="text-sm" style={{ color: 'var(--admin-faint)' }}>Données saisonnières disponibles après plusieurs mois d'activité</p>
            </div>
          )}

          {/* Event types */}
          {periods.event_types?.length > 0 && (
            <div className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
              <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--admin-gold)' }}>Répartition par type d'événement</h3>
              <div className="grid grid-cols-2 gap-2">
                {periods.event_types.map((et, i) => (
                  <div key={`event-${i}`} className="flex items-center justify-between p-2 rounded-lg" style={{ background: '#1C1C1C' }}>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{et.type}</span>
                    <span className="font-mono-ose text-sm font-bold" style={{ color: COLORS[i % COLORS.length] }}>{et.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* CLIENTS */}
      {activeTab === 'clients' && clients && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Clients total', value: clients.total_clients, color: '#C5D8F9' },
              { label: 'Clients récurrents', value: clients.clients_recurrents, color: '#3DDC97' },
              { label: 'Taux rétention', value: `${clients.taux_retention}%`, color: 'var(--admin-gold)' },
            ].map((k, i) => (
              <div key={`col-${i}`} className="p-4 rounded-xl text-center" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                <p className="text-2xl font-bold font-mono-ose" style={{ color: k.color }}>{k.value}</p>
                <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{k.label}</p>
              </div>
            ))}
          </div>

          {/* Profile repartition */}
          {clients.repartition_profils?.length > 0 && (
            <div className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
              <h3 className="font-semibold text-sm mb-3" style={{ color: 'var(--admin-gold)' }}>Répartition profils</h3>
              <div className="flex gap-2 flex-wrap">
                {clients.repartition_profils.map((p, i) => (
                  <div key={`col-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: '#1C1C1C' }}>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{p.profil}</span>
                    <span className="font-mono-ose font-bold text-sm" style={{ color: COLORS[i % COLORS.length] }}>{p.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
